/**
 * 🔴 **A MODULE FOR A TRADE IS OFFERED TO THE COMPANIES THAT PRACTISE IT** (D91).
 *
 * Devin: *"Every Company should have a managment section we need to seperate what is company
 * management from what their Busness type manages, eg Agents need to manage their artists, so that
 * should not be in the managemet module that should be outside like the production module."*
 *
 * Two halves, and the second one is this file. Company MANAGEMENT is universal — every company has a
 * record, a bank detail, a domain, users and keys — so `/management` is on every rail. What a company
 * DOES is not universal: an agency's books are to an agency what a slate is to a producer, and a
 * post house has no more use for them than for a call sheet.
 *
 * 🔴 **THIS IS NOT AUTHORISATION AND MUST NEVER BECOME IT.** `navScopes.ts` carries the same warning
 * for the same reason: the gate is the guard, six layers, re-read on every request (D41/D45), and the
 * roster endpoints refuse a company that has no business asking regardless of what is drawn. Nothing
 * here may ever be the reason something is ALLOWED.
 *
 * 🔴 **WHERE IT DIFFERS FROM `navScopes.ts`, AND WHY.** That file fails OPEN on an empty answer,
 * because a scope is a fact about a PERSON that the person cannot see or change: hiding a screen they
 * are entitled to leaves them no way to discover it exists. A network function is the opposite kind of
 * fact — the company wrote it on its own record, it is on screen in Management → Network Functions,
 * and any principal can change it in about four seconds. So an empty claim is a real answer and is
 * treated as one, and the recovery path is both short and discoverable: say what you are, and the
 * module appears.
 *
 * What is still uncertain fails open, exactly as there: until the switcher's list has been answered,
 * nothing is hidden.
 */
import { isNetworkFunction } from "@pinksy/contracts";
import type { NavBarItem } from "@/app/types";

/**
 * The route roots that only make sense for a company that claims a function, and the function each
 * one needs.
 *
 * 🔴 **Keyed by route ROOT, not by tile.** The tenant's `homePage` is data an operator edits; putting
 * the rule in it would mean a tile could be written with the gate left off, or with a function id
 * that no longer exists, and neither would be visible until an agency complained about a module they
 * cannot see. The route root is the module (`navScopes.ts` reads it the same way), and a root that is
 * not listed here is not gated at all.
 *
 * 🔴 **Deliberately short.** Every entry is a module a whole class of company should not be offered —
 * not a preference, not a feature flag, and never a way to tidy a rail somebody finds long.
 */
export const FUNCTION_FOR_MODULE: Readonly<Record<string, string>> = {
  /** The books (D79/D91) — who an agency represents. `@pinksy/artists`. */
  artists: "talent_agency",
};

export interface HeldFunctions {
  /** `NETWORK_FUNCTIONS` ids the chosen company claims (D57). */
  functions: ReadonlyArray<string>;
  /**
   * 🔴 False until the switcher's list has been answered. While false NOTHING is hidden — a rail that
   * loses a row for a moment on every boot, and grows it back, reads as a bug in the product.
   */
  known: boolean;
}

/** The first path segment — `/artists?section=books` → `artists`. Empty for `/`. */
export function moduleOfRoute(route: string): string {
  return (route.split("?")[0] ?? route).split("/").filter(Boolean)[0] ?? "";
}

/** The function this route needs, or null when it needs none. */
export function functionForRoute(route: string): string | null {
  const needed = FUNCTION_FOR_MODULE[moduleOfRoute(route)];
  // A guard against this table drifting out of the vocabulary: an id that is no longer a network
  // function gates nothing, rather than hiding a module from everybody for ever.
  return needed !== undefined && isNetworkFunction(needed) ? needed : null;
}

/** Whether this company's trade opens that route. */
export function practisesRoute(route: string, held: HeldFunctions): boolean {
  if (!held.known) return true;
  const needed = functionForRoute(route);
  return needed === null || held.functions.includes(needed);
}

/**
 * The entries this company has any use for. Same shape as `filterNavByScopes`, and applied after it:
 * a parent whose own route is gated goes whole, and a parent keeps only the children it may keep.
 */
export function filterNavByFunctions(items: NavBarItem[], held: HeldFunctions): NavBarItem[] {
  if (!held.known) return items;
  const out: NavBarItem[] = [];
  for (const item of items) {
    if (!practisesRoute(item.route, held)) continue;
    if (item.items && item.items.length > 0) {
      const children = item.items.filter((sub) => practisesRoute(sub.route, held));
      if (children.length === 0) continue;
      out.push({ ...item, items: children });
      continue;
    }
    out.push(item);
  }
  return out;
}
